// src/pages/AboutPage.js
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaUser, FaHistory, FaAward, FaUsers, FaHandshake, FaLightbulb, FaHome, FaChevronRight } from 'react-icons/fa';
import Section from '../components/common/Section';
import Heading from '../components/common/Heading';
import Button from '../components/common/Button';
import TeamSection from '../components/about/TeamSection';
import StoryTimeline from '../components/about/StoryTimeline';
import AchievementsSection from '../components/about/AchievementsSection';
import EnhancedPartnersSection from '../components/about/EnhancedPartnersSection';
import StrategicSDGsSection from '../components/about/StrategicSDGsSection';
import { setupRevealAnimations } from '../utils/helpers';

const AboutPage = () => {
  useEffect(() => {
    // Initialize animations
    setupRevealAnimations();
    
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Handle anchor links on page load
    const handleAnchorLinks = () => {
      const { hash } = window.location;
      if (hash) {
        // Wait for DOM to be fully rendered
        setTimeout(() => {
          const element = document.getElementById(hash.substring(1));
          if (element) {
            const headerOffset = 100; // Adjust based on your header height
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }, 100);
      }
    };
    
    handleAnchorLinks();
    window.addEventListener('hashchange', handleAnchorLinks);
    
    return () => {
      window.removeEventListener('hashchange', handleAnchorLinks);
    };
  }, []);
  
  return (
    <>
      <Helmet>
        <title>About Us - Afrovate Tech Hub</title>
        <meta name="description" content="Learn about Afrovate Tech Hub Sierra Leone, a youth-owned and youth-led social enterprise empowering youth through marketable digital skills." />
      </Helmet>
      
      {/* Hero Section with Background Image */}
      <section 
        className="relative pt-32 pb-16 overflow-hidden"
        style={{
          backgroundImage: 'url(/images/about/about-hero-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Dark overlay for better text visibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-dark to-primary opacity-80"></div>
        
        {/* Content */}
        <div className="container mx-auto px-4 relative z-10">
          {/* Breadcrumbs */}
          <div className="flex items-center justify-center mb-8 text-white text-sm">
            <Link to="/" className="flex items-center hover:text-secondary transition-colors">
              <FaHome className="mr-2" />
              Home
            </Link>
            <FaChevronRight className="mx-2 text-xs text-gray-300" />
            <span className="text-secondary font-medium">About</span>
          </div>
          
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              About Afrovate Tech Hub
            </h1>
            <p className="text-xl text-white text-opacity-90 max-w-3xl mx-auto">
              Afrovate Tech Hub Sierra Leone, established in November 2020 and formally incorporated in January 2021, 
              is a youth-owned and youth-led social enterprise empowering youth through marketable digital skills.
            </p>
          </div>
        </div>
      </section>
      
      {/* Our Story Section - Added ID here */}
      <Section background="white" padding="lg" id="our-story">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="reveal reveal-delay-1">
            <Heading level={2} withAccent>
              Our Story
            </Heading>
            <p className="text-gray-600 mb-4">
              Afrovate Tech Hub was established in November 2020 to equip STEM youths with "Skills Against Poverty," 
              underpinning the role of adequately skilled youths in poverty alleviation. We were formally 
              registered and incorporated in January 2021 with the Corporate Affairs Commission under the 
              Company's Registration Act of Sierra Leone.
            </p>
            <p className="text-gray-600 mb-6">
              We operate as a social enterprise, prioritising societal impact over profit. Since 2021, 
              through our three-month cohort-based micro-credential training programs, Afrovate Tech Hub has trained 
              13 Cohorts of over 1000+ youths in various marketable STEM and digital skills.
            </p>
            <Button 
              variant="primary" 
              to="#our-journey"
              icon={<FaHistory className="ml-2" />}
            >
              Explore Our Journey
            </Button>
          </div>
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="bg-gray-100 p-4 rounded-lg shadow-lg">
              <img 
                src="/images/about/our-story-image.jpg" 
                alt="Afrovate Tech Hub Story" 
                className="rounded-lg w-full h-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://placehold.co/600x400/1a237e/ffffff?text=Our+Story";
                }}
              />
            </div>
            
            {/* Floating stat card */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              viewport={{ once: true }}
              className="absolute -bottom-10 -left-10 bg-white p-6 rounded-lg shadow-xl border-l-4 border-secondary"
            >
              <div className="text-4xl font-bold text-primary-dark">1000+</div>
              <div className="text-gray-600">Successful Trainees</div>
            </motion.div>
            
            {/* Floating stat card */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7, duration: 0.5 }}
              viewport={{ once: true }}
              className="absolute -top-8 -right-8 bg-white p-6 rounded-lg shadow-xl border-l-4 border-primary"
            >
              <div className="text-4xl font-bold text-primary-dark">13</div>
              <div className="text-gray-600">Training Cohorts</div>
            </motion.div>
          </motion.div>
        </div>
      </Section>
      
      {/* Vision & Mission Section - Added ID here */}
      <Section background="gray" padding="lg" id="vision-mission">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-white p-8 rounded-lg shadow-md h-full"
          >
            <div className="flex items-center mb-6">
              <div className="h-16 w-16 bg-primary-light bg-opacity-20 rounded-full flex items-center justify-center mr-4">
                <FaLightbulb className="h-8 w-8 text-primary" />
              </div>
              <Heading level={3}>
                Our Vision
              </Heading>
            </div>
            <p className="text-gray-600 mb-6 text-lg leading-relaxed">
              We are committed to making Sierra Leone a hub for youth-led entrepreneurship, 
              innovation and TVET skills in Africa.
            </p>
            
            <div className="mt-6 bg-gray-50 p-4 rounded-lg border-l-4 border-primary">
              <h4 className="font-semibold text-primary-dark mb-2">Our Impact Goal</h4>
              <p className="text-gray-700">10,000 trained youths with marketable digital skills by 2030</p>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-white p-8 rounded-lg shadow-md h-full"
          >
            <div className="flex items-center mb-6">
              <div className="h-16 w-16 bg-secondary bg-opacity-20 rounded-full flex items-center justify-center mr-4">
                <FaAward className="h-8 w-8 text-secondary" />
              </div>
              <Heading level={3}>
                Our Mission
              </Heading>
            </div>
            <p className="text-gray-600 mb-4 text-lg leading-relaxed">
              We are putting all human efforts through 21st-century TVET capacity building in Science, Technology, 
              Engineering and Mathematics (STEM) and innovation to catapult Sierra Leone to the top of the human 
              development index, spiking skills development for financial success, entrepreneurship drive, 
              employability and professional competence among youths.
            </p>
          </motion.div>
        </div>
      </Section>
      
      {/* Values Section */}
      <Section background="white" padding="lg">
        <div className="text-center mb-12">
          <Heading level={2} align="center" className="mb-4" withAccent>
            Our Core Values
          </Heading>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            The principles that guide our work and define our culture
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { 
              title: 'Innovation', 
              icon: <FaLightbulb />, 
              color: 'bg-blue-500',
              description: 'We embrace creative solutions and cutting-edge approaches to training and technology.' 
            },
            { 
              title: 'Creativity', 
              icon: <FaLightbulb />, 
              color: 'bg-indigo-500',
              description: 'We encourage original thinking and novel approaches to problem-solving.' 
            },
            { 
              title: 'Youths First', 
              icon: <FaUsers />, 
              color: 'bg-green-500',
              description: 'We prioritize the needs, growth, and success of young people in all our initiatives.' 
            },
            { 
              title: 'Diversity and Inclusivity', 
              icon: <FaUsers />, 
              color: 'bg-yellow-500',
              description: 'We welcome and value people from all backgrounds, perspectives, and experiences.' 
            },
            { 
              title: 'Impact Over Profit', 
              icon: <FaHandshake />, 
              color: 'bg-red-500',
              description: 'As a social enterprise, our primary focus is creating positive change rather than financial gain.' 
            },
            { 
              title: 'High Moral Standards', 
              icon: <FaAward />, 
              color: 'bg-purple-500',
              description: 'We uphold integrity, honesty, and ethics in all our operations and interactions.' 
            }
          ].map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden transform transition-all duration-300 hover:-translate-y-2 hover:shadow-lg"
            >
              <div className={`${value.color} h-2`}></div>
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className={`h-10 w-10 ${value.color} bg-opacity-20 rounded-full flex items-center justify-center mr-3`}>
                    <span className="text-xl text-primary-dark">{value.icon}</span>
                  </div>
                  <h3 className="text-xl font-bold text-primary-dark">{value.title}</h3>
                </div>
                <p className="text-gray-600">{value.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>
      
      {/* Achievements Section */}
      <div id="achievements-section">
        <AchievementsSection background="gray" />
      </div>
      
      {/* Strategic SDGs Section - Added ID here */}
      <div id="sdgs-section">
        <StrategicSDGsSection />
      </div>
      
      {/* Timeline Section */}
      <div id="our-journey">
        <StoryTimeline />
      </div>
      
      {/* Team Section - Added ID here */}
      <div id="team-section">
        <TeamSection />
      </div>
      
      {/* Partners Section */}
      <EnhancedPartnersSection />
      
      {/* The Leotech Rationale Section */}
      <Section background="white" padding="lg">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <Heading level={2} withAccent className="mb-6">
              The Afrovate Tech Hub Rationale
            </Heading>
            
            <div className="space-y-6">
              <p className="text-gray-700">
              Unemployment is a major challenge for young people in Sierra Leone, with the United Nations Development Programme estimating that 60% of economically active youth are unemployed. This situation exacerbates poverty, leading to social issues like hooliganism, drug abuse, and illegal migration, robbing youths of dignity, economic participation, self-confidence, and decision-making rights. 
              </p>
              
              <p className="text-gray-700">
              This is critical, as youths comprise over 50% of Sierra Leone's population. To secure Sierra Leone's future, youths must access decent jobs, build businesses, and become economically active. At Afrovate Tech Hub Sierra Leone, we tackle this problem in a radical youth-for-youth approach through our "Skills Against Poverty" initiative, equipping youth with technological and entrepreneurship skills. 
              </p>
              
              <p className="text-gray-700">
              Our successful trainees leverage these skills to create jobs for themselves and others. At Afrovate Tech Hub, we offer hands-on training in digital technology, Engineering Design, green skills, and mentorship to empower youths, unlocking their potential and fostering socio-economic development in their communities.  
              </p>
            </div>
            
            <div className="mt-8">
              <Button 
                variant="primary" 
                to="/programs"
                icon={<FaChevronRight className="ml-2" />}
              >
                Explore Our Programs
              </Button>
            </div>
          </div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="rounded-lg overflow-hidden shadow-xl"
          >
            <img 
              src="/images/about/leotech-training.jpg" 
              alt="Afrovate Tech Hub Training Session" 
              className="w-full h-auto"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://placehold.co/800x500/1a237e/ffffff?text=Afrovate+Tech+Hub+Training";
              }}
            />
          </motion.div>
        </div>
      </Section>
      
      {/* Call to Action */}
      <Section background="gradient-primary" padding="lg">
        <div className="text-center max-w-3xl mx-auto">
          <Heading level={2} color="white" align="center" className="mb-6">
            Join the Afrovate Tech Hub Community
          </Heading>
          <p className="text-xl text-white text-opacity-90 mb-10">
            Whether you're a student, graduate, industry partner, or supporter,
            there are many ways to get involved with our mission to empower youth through digital skills.
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <Button 
              variant="secondary" 
              size="lg" 
              to="/programs"
            >
              Explore Programs
            </Button>
            <Button 
              variant="light" 
              size="lg" 
              to="/contact"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
};

export default AboutPage;